/* utils/services/What3wordsService.ts --------------------------------------- *
 * what3words in the web's place searches (site search, the planner's start  *
 * and end fields). Typing `///` switches a search into what3words mode;     *
 * suggestions come from autosuggest as the words are typed, and only the    *
 * one picked is converted to coordinates. Both go through our proxy         *
 * (ApiProxy::What3words), so the key stays on the server.                   *
 * ------------------------------------------------------------------------- */

import { apiProxyHeaders, apiProxyUrl } from '../apiProxy';

/** Suggestions asked for per lookup. */
const SUGGESTIONS = 3;

/** One letter or more in any script — what3words has addresses in dozens of languages. */
const WORD = '\\p{L}+';
/**
 * Two words and the dot after them: what3words starts suggesting from there,
 * before any of the third word is typed — which is also what shows people the
 * search autocompletes.
 */
const SUGGESTIBLE = new RegExp(`^${WORD}\\.${WORD}\\.(?:${WORD})?$`, 'u');

/**
 * A shared what3words link (w3w.co/…, what3words.com/…) counts as typing
 * `///` in front of its words.
 */
const LINK_PREFIX = /^(?:https?:\/\/)?(?:www\.)?(?:w3w\.co|what3words\.com|map\.what3words\.com)\//i;

export interface W3wQuery {
  /** What's been typed after the `///`, lowercased, its words joined by dots: "filled.count.so". */
  words: string;
  /** Two words and a dot, or more — enough to ask autosuggest about. */
  ready: boolean;
}

export interface W3wSuggestion {
  words: string;
  nearestPlace: string;
  /** ISO 3166-1 alpha-2, upper case ("GB"), or "ZZ" at sea. */
  country: string;
}

export interface W3wLocation {
  words: string;
  lng: number;
  lat: number;
  nearestPlace: string;
}

/**
 * The what3words address being typed, or null when the text isn't one. Only
 * text that starts with `///` (or is a what3words link) counts, so ordinary
 * searches are never sent to what3words. Spaces between words are taken as
 * dots, as people type them.
 */
export function parseW3wQuery(text: string): W3wQuery | null {
  const trimmed = text.trim();
  let rest: string;
  if (trimmed.startsWith('///')) {
    rest = trimmed.slice(3);
  } else if (LINK_PREFIX.test(trimmed)) {
    rest = trimmed.replace(LINK_PREFIX, '').replace(/^\/{0,3}/, '');
  } else {
    return null;
  }
  const words = rest.trim().toLowerCase().replace(/[\s.,]+/g, '.');
  return { words, ready: SUGGESTIBLE.test(words) };
}

/**
 * Three whole words and nothing else: what an address copied without its
 * slashes looks like. Three letters a word at least, and never `www.`, so a
 * typed web address (bbc.co.uk, www.hiiker.app) isn't taken for one; the
 * nudge is only a suggestion, so a rare miss costs nothing.
 */
const BARE_WORD = '\\p{L}{3,}';
const BARE_ADDRESS = new RegExp(`^(?!www\\.)${BARE_WORD}\\.${BARE_WORD}\\.${BARE_WORD}$`, 'u');

/**
 * A nudge towards what3words mode, offered only when what's typed suggests
 * the person is already thinking in what3words — so nobody else sees it:
 *  - `address`: three words with dots and no slashes, as copied out of the
 *    what3words app or a message;
 *  - `slashes`: one or two slashes at the start, short of the three.
 * `query` is what the search becomes when the nudge is taken.
 */
export interface W3wNudge {
  kind: 'address' | 'slashes';
  query: string;
}

export function w3wNudge(text: string): W3wNudge | null {
  const trimmed = text.trim();
  if (parseW3wQuery(trimmed)) return null;
  const slashes = /^\/{1,2}(?!\/)/.exec(trimmed);
  if (slashes) return { kind: 'slashes', query: `///${trimmed.slice(slashes[0].length).trimStart()}` };
  const words = trimmed.toLowerCase();
  if (BARE_ADDRESS.test(words)) return { kind: 'address', query: w3wLabel(words) };
  return null;
}

/** "///filled.count.soap" — how an address is written wherever it's shown. */
export function w3wLabel(words: string): string {
  return `///${words}`;
}

export function w3wAutosuggestUrl(words: string, focus: number[] | null = null): string {
  const params = new URLSearchParams({ input: words, 'n-results': String(SUGGESTIONS) });
  if (focus && Number.isFinite(focus[0]) && Number.isFinite(focus[1])) {
    params.set('focus', `${focus[1]},${focus[0]}`);
  }
  return apiProxyUrl('what3words', 'autosuggest', params.toString());
}

export function w3wCoordinatesUrl(words: string): string {
  return apiProxyUrl('what3words', 'convert-to-coordinates', new URLSearchParams({ words }).toString());
}

async function getW3w(url: string): Promise<any> {
  const response = await fetch(url, { headers: { Accept: 'application/json', ...apiProxyHeaders() } });
  if (!response.ok) throw new Error(`what3words failed: ${response.status}`);
  return response.json();
}

/**
 * Addresses close to the words typed, nearest `focus` ([lon, lat]) first
 * when there is one. Throws when the lookup fails.
 */
export async function w3wAutosuggest(words: string, focus: number[] | null = null): Promise<W3wSuggestion[]> {
  const data = await getW3w(w3wAutosuggestUrl(words, focus));
  const suggestions = Array.isArray(data?.suggestions) ? data.suggestions : [];
  return suggestions
    .filter((s: any) => typeof s?.words === 'string')
    .map((s: any) => ({ words: s.words, nearestPlace: s.nearestPlace ?? '', country: s.country ?? '' }));
}

/** Where an address is. Throws when the lookup fails or the words aren't an address. */
export async function w3wToCoordinates(words: string): Promise<W3wLocation> {
  const data = await getW3w(w3wCoordinatesUrl(words));
  const lng = Number(data?.coordinates?.lng);
  const lat = Number(data?.coordinates?.lat);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) throw new Error('what3words returned no coordinates');
  return { words: data.words ?? words, lng, lat, nearestPlace: data.nearestPlace ?? '' };
}
